import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_API_URL || '';
const TOTAL_SPOTS = 50;
const DEFAULT_REMAINING = 46;
const LAUNCH_DATE = new Date('2026-04-26T00:00:00');

const gold = '#2B7FFF';
const goldLight = '#5599FF';
const goldDark = '#1A5FCC';
const bg = '#050810';
const surface = '#0B1120';
const surfaceCard = '#0F1929';
const border = 'rgba(255,255,255,0.07)';

const styles = {
  page: {
    background: 'transparent',
    color: '#fff',
    minHeight: '100vh',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    overflowX: 'hidden',
  },

  // ── HERO ──────────────────────────────────────────────────────────────
  hero: {
    position: 'relative',
    padding: '100px 24px 80px',
    textAlign: 'center',
    maxWidth: 780,
    margin: '0 auto',
  },
  badge: {
    display: 'inline-block',
    background: 'rgba(43,127,255,0.12)',
    border: `1px solid rgba(43,127,255,0.4)`,
    color: gold,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.15em',
    padding: '6px 18px',
    borderRadius: 100,
    marginBottom: 28,
    textTransform: 'uppercase',
  },
  headline: {
    fontSize: 'clamp(40px, 8vw, 72px)',
    fontWeight: 800,
    lineHeight: 1.05,
    letterSpacing: '-0.03em',
    margin: '0 0 20px',
  },
  sub: {
    fontSize: 'clamp(17px, 2.5vw, 22px)',
    color: '#9CA3B4',
    lineHeight: 1.6,
    margin: '0 0 40px',
    maxWidth: 540,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  spotsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 20,
  },
  spotsText: {
    fontSize: 15,
    fontWeight: 600,
    color: gold,
  },
  spotsDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: gold,
    animation: 'pulse 1.4s ease-in-out infinite',
  },
  launchText: {
    fontSize: 13,
    color: '#7A8499',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },

  // ── COUNTDOWN ─────────────────────────────────────────────────────────
  countdown: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
    margin: '36px auto 0',
    flexWrap: 'wrap',
  },
  countUnit: {
    background: surfaceCard,
    border: `1px solid ${border}`,
    borderRadius: 12,
    padding: '14px 20px',
    minWidth: 72,
    textAlign: 'center',
  },
  countNum: {
    fontSize: 28,
    fontWeight: 800,
    color: gold,
    lineHeight: 1,
    display: 'block',
  },
  countLabel: {
    fontSize: 10,
    color: '#7A8499',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginTop: 6,
    display: 'block',
  },

  // ── SECTION ───────────────────────────────────────────────────────────
  section: {
    maxWidth: 1040,
    margin: '0 auto',
    padding: '80px 24px',
  },
  sectionTitle: {
    fontSize: 'clamp(26px, 4vw, 38px)',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    textAlign: 'center',
    marginBottom: 48,
  },

  // ── BENEFIT CARDS ─────────────────────────────────────────────────────
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 20,
  },
  card: {
    background: surfaceCard,
    border: `1px solid ${border}`,
    borderRadius: 16,
    padding: '28px 24px',
    transition: 'border-color 0.2s, transform 0.2s',
    cursor: 'default',
  },
  cardIcon: {
    fontSize: 32,
    marginBottom: 14,
    display: 'block',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 8,
    color: '#fff',
  },
  cardDesc: {
    fontSize: 14,
    color: '#8892A4',
    lineHeight: 1.6,
  },

  // ── PRICING ───────────────────────────────────────────────────────────
  pricingBox: {
    background: surfaceCard,
    border: `1px solid rgba(43,127,255,0.3)`,
    borderRadius: 24,
    padding: '52px 40px',
    maxWidth: 520,
    margin: '0 auto',
    textAlign: 'center',
    boxShadow: `0 0 60px rgba(43,127,255,0.08)`,
  },
  priceTag: {
    fontSize: 'clamp(64px, 12vw, 96px)',
    fontWeight: 900,
    color: gold,
    lineHeight: 1,
    letterSpacing: '-0.04em',
  },
  priceStrike: {
    fontSize: 22,
    color: '#6B7280',
    textDecoration: 'line-through',
    marginLeft: 8,
    verticalAlign: 'middle',
  },
  priceSub: {
    fontSize: 15,
    color: '#8892A4',
    marginTop: 10,
    marginBottom: 36,
  },
  ctaBtn: {
    display: 'inline-block',
    background: `linear-gradient(135deg, ${gold} 0%, ${goldLight} 50%, ${goldDark} 100%)`,
    color: '#fff',
    fontWeight: 800,
    fontSize: 18,
    padding: '18px 48px',
    borderRadius: 100,
    border: 'none',
    cursor: 'pointer',
    letterSpacing: '0.01em',
    transition: 'transform 0.15s, box-shadow 0.15s',
    width: '100%',
    maxWidth: 380,
    boxShadow: `0 4px 32px rgba(43,127,255,0.3)`,
  },
  ctaNote: {
    marginTop: 18,
    fontSize: 12,
    color: '#7A8499',
    letterSpacing: '0.04em',
  },

  // ── TESTIMONIAL ───────────────────────────────────────────────────────
  testimonialWrap: {
    background: surface,
    borderTop: `1px solid ${border}`,
    borderBottom: `1px solid ${border}`,
  },
  testimonial: {
    maxWidth: 680,
    margin: '0 auto',
    padding: '72px 24px',
    textAlign: 'center',
  },
  quote: {
    fontSize: 'clamp(18px, 3vw, 26px)',
    fontStyle: 'italic',
    lineHeight: 1.55,
    color: '#ccc',
    marginBottom: 28,
  },
  quoteAuthor: {
    fontSize: 14,
    color: '#7A8499',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  quoteBar: {
    width: 40,
    height: 2,
    background: gold,
    margin: '0 auto 24px',
  },

  // ── FAQ ───────────────────────────────────────────────────────────────
  faqList: {
    maxWidth: 680,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  faqItem: {
    background: surfaceCard,
    border: `1px solid ${border}`,
    borderRadius: 14,
    padding: '24px 28px',
  },
  faqQ: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 8,
    color: '#fff',
  },
  faqA: {
    fontSize: 14,
    color: '#8892A4',
    lineHeight: 1.65,
  },

  // ── FOOTER CTA ────────────────────────────────────────────────────────
  footerCta: {
    textAlign: 'center',
    padding: '80px 24px 100px',
    maxWidth: 600,
    margin: '0 auto',
  },
};

const benefits = [
  {
    icon: '⏰',
    title: 'Your Evenings Back',
    desc: 'AI books while you\'re at dinner. Follow-ups send while you\'re with your kids. Reminders go out while you sleep. You just show up and do what you love.',
  },
  {
    icon: '💰',
    title: 'Lifetime Pro Rate — $29/mo',
    desc: 'Locked at $29/mo forever. 0% commission on every booking. After launch it\'s $39/mo. Yours never goes up.',
  },
  {
    icon: '📞',
    title: 'Personal Onboarding with Christian',
    desc: '30 min with Christian personally. A barber who built this between clients will set you up to actually flow.',
  },
  {
    icon: '🎯',
    title: 'First 5 Bookings Guaranteed',
    desc: 'We personally help you land your first 5 clients on Mane. You go live, we go to work.',
  },
  {
    icon: '🔒',
    title: 'Early Access Before Launch',
    desc: 'Full dashboard and automation access before the public. Be running on autopilot before the doors open.',
  },
  {
    icon: '🏆',
    title: 'Founding Member Badge',
    desc: 'Your profile shows the Founding Member badge forever. You were first. That means something.',
  },
];

const faqs = [
  {
    q: 'Why $199?',
    a: 'It covers your first 6 months of Pro and permanently locks your rate. After launch, Pro is $39/mo. Founding Members pay $29/mo — forever. The math is obvious.',
  },
  {
    q: 'What if I hate it?',
    a: '30-day money back guarantee. No questions asked. If Mane isn\'t what we promised, you get every dollar back.',
  },
  {
    q: 'When do I get access?',
    a: 'Immediately after purchase. Your personal onboarding call with Christian will be scheduled within 48 hours.',
  },
];

function CountdownUnit({ value, label }) {
  return (
    <div style={styles.countUnit} className="count-unit">
      <span style={styles.countNum}>{String(value).padStart(2, '0')}</span>
      <span style={styles.countLabel}>{label}</span>
    </div>
  );
}

function FoundingSuccess() {
  return (
    <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={{ textAlign: 'center', padding: '40px 24px', maxWidth: 520 }}>
        <div style={{ fontSize: 72, marginBottom: 28 }}>🎉</div>
        <h1 style={{ fontSize: 'clamp(32px, 6vw, 52px)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: 20 }}>
          You're a Founding Member.
        </h1>
        <p style={{ fontSize: 18, color: '#9CA3B4', lineHeight: 1.65, marginBottom: 40 }}>
          Check your email for next steps. Christian will reach out within 48 hours to schedule your personal onboarding call.
        </p>
        <div style={{
          background: '#0F1929',
          border: `1px solid rgba(43,127,255,0.3)`,
          borderRadius: 16,
          padding: '24px 28px',
          marginBottom: 40,
          textAlign: 'left',
        }}>
          <p style={{ fontSize: 13, color: '#7A8499', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>What happens next</p>
          {[
            'Confirmation email sent to your inbox',
            'Christian calls within 48 hours to onboard you',
            'Your profile goes live before the public launch',
            'We personally drive your first 5 bookings',
          ].map((step, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
              <span style={{ color: gold, fontSize: 13, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>0{i + 1}</span>
              <span style={{ fontSize: 14, color: '#A0AABB', lineHeight: 1.5 }}>{step}</span>
            </div>
          ))}
        </div>
        <button
          style={{ ...styles.ctaBtn, fontSize: 16, padding: '16px 36px', width: 'auto' }}
          onClick={() => window.open('https://joinmane.com', '_self')}
        >
          Join our community →
        </button>
      </div>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
      `}</style>
    </div>
  );
}

export { FoundingSuccess };

export default function Founding() {
  const [spotsRemaining, setSpotsRemaining] = useState(DEFAULT_REMAINING);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState({ days: 31, hours: 0, minutes: 0, seconds: 0 });
  const [cardHover, setCardHover] = useState(null);
  const [btnHover, setBtnHover] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/founding/spots`)
      .then(r => r.json())
      .then(data => {
        if (typeof data.spots_remaining === 'number') {
          setSpotsRemaining(data.spots_remaining);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const diff = LAUNCH_DATE - now;
      if (diff <= 0) return;
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleClaim = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/founding/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setLoading(false);
      }
    } catch {
      setLoading(false);
    }
  };

  const spotsBar = Math.round(((TOTAL_SPOTS - spotsRemaining) / TOTAL_SPOTS) * 100);

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Founding Member — Mane</title>
        <meta name="description" content="Join the first 50 founding members of Mane. Lock in lifetime Pro pricing at $29/mo forever. Built for service professionals." />
        <meta property="og:title" content="Become a Mane Founding Member" />
        <meta property="og:description" content="50 spots. Lifetime Pro pricing. $29/mo forever. Built for professionals who got tired of platform fees." />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://joinmane.com/og-image-v8.png" />
      </Helmet>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.6;transform:scale(1.15)} }
        @keyframes shimmer {
          0%{background-position:200% center}
          100%{background-position:-200% center}
        }
        * { box-sizing: border-box; }
        body { margin: 0; }
        @media (max-width: 600px) {
          .pricing-box { padding: 32px 16px !important; }
          .cta-btn { font-size: 15px !important; padding: 16px 20px !important; width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
        }
        @media (max-width: 480px) {
          h1 { word-break: break-word; }
          .founding-grid { grid-template-columns: 1fr !important; }
          .countdown-wrap { gap: 8px !important; }
          .count-unit { min-width: 60px !important; padding: 10px 12px !important; }
        }
      `}</style>

      {/* ── HERO ── */}
      <div style={styles.hero}>
        <div style={styles.badge}>50 Founding Members · Your Business, On Autopilot</div>
        <h1 style={styles.headline}>
          Run your business.<br />Live your life.
        </h1>
        <p style={styles.sub}>
          You went independent to be free. But you're still chained to your phone. Be one of 50 founding professionals who automates the grind — and actually gets to live.
        </p>

        {/* Spots counter */}
        <div style={styles.spotsRow}>
          <span style={styles.spotsDot} />
          <span style={styles.spotsText}>
            {spotsRemaining} of {TOTAL_SPOTS} spots remaining
          </span>
        </div>

        {/* Progress bar */}
        <div style={{ width: '100%', maxWidth: 340, margin: '0 auto 20px', background: 'rgba(255,255,255,0.05)', borderRadius: 100, height: 6, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${spotsBar}%`,
            background: `linear-gradient(90deg, ${gold}, ${goldLight})`,
            borderRadius: 100,
            transition: 'width 1s ease',
          }} />
        </div>

        <div style={styles.launchText}>Launch: April 26, 2026</div>

        {/* Countdown */}
        <div style={styles.countdown} className="countdown-wrap">
          <CountdownUnit value={countdown.days} label="Days" />
          <CountdownUnit value={countdown.hours} label="Hours" />
          <CountdownUnit value={countdown.minutes} label="Minutes" />
          <CountdownUnit value={countdown.seconds} label="Seconds" />
        </div>
      </div>

      {/* ── WHAT YOU GET ── */}
      <div style={{ background: surface, borderTop: `1px solid ${border}`, borderBottom: `1px solid ${border}` }}>
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>What you get as a Founding Member</h2>
          <div style={styles.grid} className="founding-grid">
            {benefits.map((b, i) => (
              <div
                key={i}
                style={{
                  ...styles.card,
                  borderColor: cardHover === i ? `rgba(43,127,255,0.4)` : border,
                  transform: cardHover === i ? 'translateY(-3px)' : 'none',
                }}
                onMouseEnter={() => setCardHover(i)}
                onMouseLeave={() => setCardHover(null)}
              >
                <span style={styles.cardIcon}>{b.icon}</span>
                <div style={styles.cardTitle}>{b.title}</div>
                <div style={styles.cardDesc}>{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── PRICING ── */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>One payment. Lifetime advantage.</h2>
        <div style={{ ...styles.pricingBox }} className="pricing-box">
          <div style={{ marginBottom: 8 }}>
            <span style={styles.priceTag}>$199</span>
            <span style={styles.priceStrike}>$499</span>
          </div>
          <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4, textDecoration: 'line-through' }}>one-time</div>
          <p style={styles.priceSub}>Then $29/mo Pro plan — locked in forever</p>

          {/* Checklist */}
          <div style={{ textAlign: 'left', marginBottom: 36, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              'Founding Member Badge on your profile',
              'Pro plan at $29/mo — never goes up',
              '30-min personal onboarding with Christian',
              'First 5 bookings guaranteed',
              'Early access before public launch',
              'Featured in launch marketing',
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: gold, fontSize: 16, flexShrink: 0 }}>✓</span>
                <span style={{ fontSize: 14, color: '#bbb' }}>{item}</span>
              </div>
            ))}
          </div>

          <button
            className="cta-btn"
            style={{
              ...styles.ctaBtn,
              transform: btnHover && !loading ? 'scale(1.03)' : 'scale(1)',
              boxShadow: btnHover && !loading
                ? `0 8px 40px rgba(43,127,255,0.45)`
                : `0 4px 32px rgba(43,127,255,0.3)`,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'wait' : 'pointer',
            }}
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
            onClick={handleClaim}
            disabled={loading}
          >
            {loading ? 'Redirecting...' : 'Claim Your Spot →'}
          </button>

          <p style={styles.ctaNote}>
            Only {TOTAL_SPOTS} spots · {spotsRemaining} remaining · April 26 launch · 30-day guarantee
          </p>
        </div>
      </div>

      {/* ── TESTIMONIAL ── */}
      <div style={styles.testimonialWrap}>
        <div style={styles.testimonial}>
          <div style={styles.quoteBar} />
          <p style={styles.quote}>
            "I built Mane because it was taking away from my family time. I couldn't find a way to automate my life where I could just flow and live. I wanted to be free and know my life was still heading in the right direction."
          </p>
          <div style={styles.quoteAuthor}>— Christian, founder & working barber</div>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div style={styles.section}>
        <h2 style={{ ...styles.sectionTitle, marginBottom: 36 }}>Common questions</h2>
        <div style={styles.faqList}>
          {faqs.map((f, i) => (
            <div key={i} style={styles.faqItem}>
              <div style={styles.faqQ}>{f.q}</div>
              <div style={styles.faqA}>{f.a}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FOOTER CTA ── */}
      <div style={{ background: surface, borderTop: `1px solid ${border}` }}>
        <div style={styles.footerCta}>
          <div style={styles.badge}>Only {spotsRemaining} spots left</div>
          <h2 style={{ ...styles.headline, fontSize: 'clamp(32px, 5vw, 52px)', marginBottom: 20 }}>
            Run your business.<br />Live your life.
          </h2>
          <p style={{ ...styles.sub, marginBottom: 36 }}>
            50 founding spots. Your business automated. Your time back. Your family first.
          </p>
          <button
            style={{
              ...styles.ctaBtn,
              fontSize: 18,
              transform: btnHover ? 'scale(1.03)' : 'scale(1)',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
            onClick={handleClaim}
            disabled={loading}
          >
            {loading ? 'Redirecting...' : 'Claim Your Spot →'}
          </button>
          <p style={{ marginTop: 16, fontSize: 12, color: '#6B7280' }}>
            30-day money back guarantee · No questions asked
          </p>
        </div>
      </div>
    </div>
  );
}
